import React, { useMemo, useState } from 'react';
import { Search, Eye, AlertCircle, CheckCircle, Layout, X, ChevronLeft } from 'lucide-react';
import { Question, ModuleId } from '../../types';
import { MODULES } from '../../data/modules';
import { LEVELS } from '../../config/levels';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { QuestionCardClean } from '../practice/QuestionCardClean';
import { QuestionRendererFactory } from '../practice/renderers/QuestionRendererFactory';
interface QuestionBrowserProps {
  questions: Question[];
}
export function QuestionBrowser({
  questions
}: QuestionBrowserProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState<ModuleId | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [previewQuestion, setPreviewQuestion] = useState<Question | null>(null);
  const filteredQuestions = useMemo(() => {
    return questions.filter(q => {
      const matchesSearch = q.question.toLowerCase().includes(searchTerm.toLowerCase()) || q.id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesModule = selectedModule === 'all' || q.module === selectedModule;
      const matchesLevel = selectedLevel === 'all' || q.difficulty != null && q.difficulty.toString() === selectedLevel;
      return matchesSearch && matchesModule && matchesLevel;
    });
  }, [questions, searchTerm, selectedModule, selectedLevel]);
  return <div className="flex flex-col h-full" data-id="element-727">
      {/* Compact Filters */}
      <div className="flex-none p-4 border-b border-slate-200 bg-white space-y-3" data-id="element-728">
        <div className="flex gap-3 flex-wrap" data-id="element-729">
          <div className="relative flex-1 min-w-[200px]" data-id="element-730">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-731" />
            <input type="text" placeholder="Zoek op vraag of ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-732" />
          </div>
          <select value={selectedModule} onChange={e => setSelectedModule(e.target.value as ModuleId | 'all')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-733">
            <option value="all" data-id="element-734">Alle Vakken</option>
            {MODULES.map(m => <option key={m.id} value={m.id} data-id="element-735">
                {m.title}
              </option>)}
          </select>
          <select value={selectedLevel} onChange={e => setSelectedLevel(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-736">
            <option value="all" data-id="element-737">Alle Niveaus</option>
            {LEVELS.map(l => <option key={l.id} value={l.id.toString()} data-id="element-738">
                {l.label}
              </option>)}
          </select>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-400" data-id="element-739">
          <span data-id="element-740">{filteredQuestions.length} vragen gevonden</span>
          {previewQuestion && <button onClick={() => setPreviewQuestion(null)} className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1" data-id="element-741">
              <X className="w-3 h-3" data-id="element-742" /> Sluit preview
            </button>}
        </div>
      </div>

      {/* Content: List + Preview */}
      <div className="flex-1 min-h-0 flex" data-id="element-743">
        {/* Question List — hidden on mobile when preview is open */}
        <div className={`
          ${previewQuestion ? 'hidden lg:flex' : 'flex'}
          flex-col w-full lg:w-[340px] lg:flex-shrink-0 border-r border-slate-100 bg-white
        `} data-id="element-744">
          <div className="flex-1 overflow-y-auto" data-id="element-745">
            {filteredQuestions.slice(0, 100).map(q => <button key={q.id} onClick={() => setPreviewQuestion(q)} className={`
                  w-full text-left px-4 py-3 border-b border-slate-100 transition-colors
                  ${previewQuestion?.id === q.id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-slate-50 border-l-2 border-l-transparent'}
                `} data-id="element-746">
                <div className="flex items-center gap-1.5 mb-1 flex-wrap" data-id="element-747">
                  <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1 py-0.5 rounded" data-id="element-748">
                    {q.id}
                  </span>
                  <Badge variant="outline" size="sm" className="text-[10px]" data-id="element-749">
                    {q.module}
                  </Badge>
                  <Badge variant="secondary" size="sm" className="text-[10px]" data-id="element-750">
                    Lvl {q.difficulty}
                  </Badge>
                </div>
                <p className="text-sm text-slate-800 line-clamp-2 leading-snug" data-id="element-751">
                  {q.question}
                </p>
              </button>)}
            {filteredQuestions.length > 100 && <div className="text-center py-4 text-slate-400 text-xs" data-id="element-752">
                +{filteredQuestions.length - 100} meer (gebruik filters)
              </div>}
            {filteredQuestions.length === 0 && <div className="text-center py-12 text-slate-400 text-sm" data-id="element-753">
                Geen vragen gevonden
              </div>}
          </div>
        </div>

        {/* Preview Pane */}
        <div className={`
          ${previewQuestion ? 'flex' : 'hidden lg:flex'}
          flex-col flex-1 min-w-0 bg-slate-50
        `} data-id="element-754">
          {previewQuestion ? <div className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-755">
              {/* Back button on mobile/tablet */}
              <button onClick={() => setPreviewQuestion(null)} className="lg:hidden flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-id="element-756">
                <ChevronLeft className="w-4 h-4" data-id="element-757" /> Terug naar lijst
              </button>

              <div className="max-w-2xl mx-auto space-y-5" data-id="element-758">
                {/* Preview header */}
                <div className="flex items-center justify-between" data-id="element-759">
                  <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2" data-id="element-760">
                    <Layout className="w-4 h-4" data-id="element-761" />
                    Live Preview
                  </h3>
                  <div className="flex gap-2" data-id="element-762">
                    {previewQuestion.explanation ? <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100" data-id="element-763">
                        <CheckCircle className="w-3 h-3" data-id="element-764" /> Uitleg
                      </span> : <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100" data-id="element-765">
                        <AlertCircle className="w-3 h-3" data-id="element-766" /> Geen uitleg
                      </span>}
                  </div>
                </div>

                {/* Rendered Question */}
                <QuestionCardClean question={previewQuestion} visualAid={previewQuestion.visualAid} data-id="element-767">
                  <QuestionRendererFactory question={previewQuestion} onAnswer={() => {}} data-id="element-768" />
                </QuestionCardClean>

                {/* Debug Info */}
                <div className="bg-slate-800 text-slate-200 rounded-xl p-4" data-id="element-769">
                  <h4 className="font-mono text-[10px] font-bold text-slate-400 uppercase mb-3 border-b border-slate-700 pb-2" data-id="element-770">
                    Debug Data
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-xs font-mono" data-id="element-771">
                    <div data-id="element-772">
                      <span className="text-slate-500 block text-[10px]" data-id="element-773">
                        Type
                      </span>
                      <span className="text-indigo-400" data-id="element-774">
                        {previewQuestion.questionType || 'multiple_choice'}
                      </span>
                    </div>
                    <div data-id="element-775">
                      <span className="text-slate-500 block text-[10px]" data-id="element-776">
                        Family ID
                      </span>
                      <span className="text-emerald-400" data-id="element-777">
                        {previewQuestion.familyId || '-'}
                      </span>
                    </div>
                    <div data-id="element-778">
                      <span className="text-slate-500 block text-[10px]" data-id="element-779">
                        Skill
                      </span>
                      <span className="text-amber-400" data-id="element-780">
                        {previewQuestion.skill || '-'}
                      </span>
                    </div>
                    <div data-id="element-781">
                      <span className="text-slate-500 block text-[10px]" data-id="element-782">
                        SubSkill
                      </span>
                      <span className="text-amber-400" data-id="element-783">
                        {previewQuestion.subSkill || '-'}
                      </span>
                    </div>
                  </div>
                  {previewQuestion.interactiveConfig && <div className="mt-3 pt-3 border-t border-slate-700" data-id="element-784">
                      <span className="text-slate-500 block text-[10px] font-mono mb-1" data-id="element-785">
                        Interactive Config
                      </span>
                      <pre className="bg-slate-900 p-2 rounded text-[10px] overflow-x-auto text-slate-300" data-id="element-786">
                        {JSON.stringify(previewQuestion.interactiveConfig, null, 2)}
                      </pre>
                    </div>}
                </div>

                {/* Explanation */}
                {previewQuestion.explanation && <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4" data-id="element-787">
                    <h4 className="font-bold text-emerald-800 text-sm mb-1" data-id="element-788">
                      Uitleg
                    </h4>
                    <p className="text-emerald-900 text-sm" data-id="element-789">
                      {previewQuestion.explanation}
                    </p>
                  </div>}
              </div>
            </div> : <div className="flex-1 flex items-center justify-center text-slate-400" data-id="element-790">
              <div className="text-center" data-id="element-791">
                <Eye className="w-10 h-10 mx-auto mb-3 opacity-30" data-id="element-792" />
                <p className="text-sm" data-id="element-793">
                  Selecteer een vraag om een preview te zien
                </p>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}